import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MarketingNav } from '@/components/landing/MarketingNav';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { Button } from '@/components/ui/button';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { SEOHead } from '@/components/SEOHead';
import { SchemaMarkup, organizationSchema, generateBreadcrumbSchema, generateSoftwareAppSchema } from '@/components/SchemaMarkup';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import {
  BarChart3, TrendingUp, Brain, BookOpen, Plug, PieChart,
  CheckCircle, ArrowRight, Shield, Zap, Target, Users
} from 'lucide-react';

// PT screenshots (full)
import ptRevenue from '@/assets/screenshots/pt/revenue-overview.png';
import ptSalesAnalytics from '@/assets/screenshots/pt/sales-analytics.png';
import ptBurndown from '@/assets/screenshots/pt/sales-burndown.png';
import ptAiAdvisor from '@/assets/screenshots/pt/ai-advisor.png';
import ptPlaybooks from '@/assets/screenshots/pt/playbooks.png';
import ptIntegrations from '@/assets/screenshots/pt/integrations.png';

// EN screenshots (full, fallback to PT when not available)
import enRevenue from '@/assets/screenshots/en/revenue-overview.png';
import enBurndown from '@/assets/screenshots/en/sales-burndown.png';

// PT thumbnails
import ptRevenueThumb from '@/assets/screenshots/pt/thumb/revenue-overview.jpg';
import ptSalesAnalyticsThumb from '@/assets/screenshots/pt/thumb/sales-analytics.jpg';
import ptBurndownThumb from '@/assets/screenshots/pt/thumb/sales-burndown.jpg';
import ptAiAdvisorThumb from '@/assets/screenshots/pt/thumb/ai-advisor.jpg';
import ptPlaybooksThumb from '@/assets/screenshots/pt/thumb/playbooks.jpg';
import ptIntegrationsThumb from '@/assets/screenshots/pt/thumb/integrations.jpg';

// EN thumbnails
import enRevenueThumb from '@/assets/screenshots/en/thumb/revenue-overview.jpg';
import enBurndownThumb from '@/assets/screenshots/en/thumb/sales-burndown.jpg';

const features = [
  {
    icon: BarChart3,
    titleKey: 'Revenue Overview',
    titleKeyPt: 'Visão de Receita',
    fullPt: ptRevenue, fullEn: enRevenue,
    thumbPt: ptRevenueThumb, thumbEn: enRevenueThumb,
    descEn: 'Get a complete view of your revenue performance in real time. Track actuals against targets, monitor pipeline coverage health, and understand exactly where your revenue is coming from — all in one executive-grade dashboard. For example, a VP of Sales can open RevOps AI on Monday morning and instantly see whether the team is on track for the quarter, which pipeline segments are underperforming, and what corrective actions to take before the gap widens.',
    descPt: 'Tenha uma visão completa da performance de receita em tempo real. Acompanhe realizado vs metas, monitore a saúde da cobertura de pipeline e entenda exatamente de onde vem sua receita — tudo em um dashboard de nível executivo. Por exemplo, um VP de Vendas pode abrir o RevOps AI na segunda-feira e ver instantaneamente se o time está no caminho certo para o trimestre, quais segmentos de pipeline estão abaixo do esperado e quais ações corretivas tomar antes que o gap aumente.',
    bullets: [
      { en: 'Real-time revenue tracking with target comparison', pt: 'Acompanhamento de receita em tempo real com comparação de metas' },
      { en: 'Pipeline coverage analysis with health indicators', pt: 'Análise de cobertura de pipeline com indicadores de saúde' },
      { en: 'Executive KPI cards with trend analysis', pt: 'Cards de KPI executivos com análise de tendência' },
      { en: 'Monthly/quarterly revenue breakdown by source', pt: 'Breakdown mensal/trimestral de receita por fonte' },
    ],
  },
  {
    icon: PieChart,
    titleKey: 'Sales Analytics',
    titleKeyPt: 'Sales Analytics',
    fullPt: ptSalesAnalytics, fullEn: ptSalesAnalytics,
    thumbPt: ptSalesAnalyticsThumb, thumbEn: ptSalesAnalyticsThumb,
    descEn: 'Go beyond surface-level reporting. Understand why deals are won or lost, identify conversion bottlenecks by funnel stage, and benchmark individual rep performance with actionable coaching insights. A sales manager can drill into a specific stage where conversion dropped, see which reps are struggling, and immediately create a coaching plan based on data.',
    descPt: 'Vá além de relatórios superficiais. Entenda por que deals são ganhos ou perdidos, identifique gargalos de conversão por estágio de funil e compare performance individual de vendedores com insights acionáveis de coaching. Um gestor de vendas pode investigar um estágio específico onde a conversão caiu, ver quais reps estão com dificuldade e criar um plano de coaching baseado em dados.',
    bullets: [
      { en: 'Win/loss analysis with root cause identification', pt: 'Análise de ganhos/perdas com identificação de causa raiz' },
      { en: 'Sales cycle benchmarking across reps and segments', pt: 'Benchmarking de ciclo de vendas por rep e segmento' },
      { en: 'Conversion funnel visualization with bottleneck detection', pt: 'Visualização de funil de conversão com detecção de gargalos' },
      { en: 'Rep performance scorecards with coaching insights', pt: 'Scorecards de performance por rep com insights de coaching' },
    ],
  },
  {
    icon: TrendingUp,
    titleKey: 'Forecast & Burndown',
    titleKeyPt: 'Forecast & Burndown',
    fullPt: ptBurndown, fullEn: enBurndown,
    thumbPt: ptBurndownThumb, thumbEn: enBurndownThumb,
    descEn: 'Replace gut-feel predictions with data-driven forecasting. Our mathematical model uses historical conversion rates and pipeline velocity to predict revenue outcomes — updated daily and visualized through an intuitive burndown chart. Teams using data-driven forecasting report up to 30% more accurate quarterly predictions compared to manual estimates.',
    descPt: 'Substitua previsões baseadas em intuição por forecasting orientado por dados. Nosso modelo matemático usa taxas históricas de conversão e velocidade de pipeline para prever resultados de receita — atualizado diariamente e visualizado em um gráfico de burndown intuitivo. Times que usam forecasting orientado por dados reportam previsões trimestrais até 30% mais precisas comparado a estimativas manuais.',
    bullets: [
      { en: 'Mathematical forecast based on historical conversion rates', pt: 'Forecast matemático baseado em taxas históricas de conversão' },
      { en: 'Sales burndown chart updated daily', pt: 'Gráfico de burndown de vendas atualizado diariamente' },
      { en: 'Manager forecast input alongside AI predictions', pt: 'Input de forecast do gestor junto a previsões de IA' },
      { en: 'What-If scenario simulator for pipeline planning', pt: 'Simulador de cenários What-If para planejamento de pipeline' },
    ],
  },
  {
    icon: Brain,
    titleKey: 'AI Advisor',
    titleKeyPt: 'Consultor de IA',
    fullPt: ptAiAdvisor, fullEn: ptAiAdvisor,
    thumbPt: ptAiAdvisorThumb, thumbEn: ptAiAdvisorThumb,
    descEn: 'Think of it as a senior RevOps analyst embedded in your product. The AI Advisor analyzes your entire CRM dataset to surface root causes, estimate revenue impact of trends, and recommend specific next actions with clear ownership. Instead of spending hours in spreadsheets, your team gets instant answers to questions like "Why did conversion drop this month?" or "Which deals are most at risk?"',
    descPt: 'Pense como um analista sênior de RevOps incorporado no seu produto. O Consultor de IA analiza todo o dataset do seu CRM para identificar causas raiz, estimar impacto em receita das tendências e recomendar ações específicas com responsável claro. Em vez de gastar horas em planilhas, seu time recebe respostas instantâneas para perguntas como "Por que a conversão caiu este mês?" ou "Quais deals estão em maior risco?"',
    bullets: [
      { en: 'Every dashboard answers a specific business question', pt: 'Cada dashboard responde uma pergunta de negócio específica' },
      { en: 'Root cause analysis with revenue impact estimates', pt: 'Análise de causa raiz com estimativas de impacto em receita' },
      { en: 'Concrete next actions with clear ownership', pt: 'Próximas ações concretas com responsável claro' },
      { en: 'Learns from your data — gets smarter over time', pt: 'Aprende com seus dados — fica mais inteligente com o tempo' },
    ],
  },
  {
    icon: BookOpen,
    titleKey: 'Playbooks',
    titleKeyPt: 'Playbooks',
    fullPt: ptPlaybooks, fullEn: ptPlaybooks,
    thumbPt: ptPlaybooksThumb, thumbEn: ptPlaybooksThumb,
    descEn: 'Turn your best sales processes into repeatable playbooks. Define step-by-step workflows for Inbound, Outbound, SDR, and AE motions, track execution at the rep level, and get alerts when critical steps are skipped. Companies with documented sales playbooks see 33% higher win rates because every rep follows the same proven methodology.',
    descPt: 'Transforme seus melhores processos de vendas em playbooks repetíveis. Defina workflows passo-a-passo para Inbound, Outbound, SDR e AE, acompanhe execução por vendedor e receba alertas quando passos críticos são pulados. Empresas com playbooks de vendas documentados têm taxas de ganho 33% maiores porque cada rep segue a mesma metodologia comprovada.',
    bullets: [
      { en: 'Revenue playbooks based on proven methodology', pt: 'Playbooks de receita baseados em metodologia comprovada' },
      { en: 'Track execution vs results at rep level', pt: 'Acompanhe execução vs resultados por rep' },
      { en: 'SLA monitoring and alerts when steps are skipped', pt: 'Monitoramento de SLAs e alertas quando passos são pulados' },
      { en: 'Templates for Inbound, Outbound, SDR, and AE motions', pt: 'Templates para Inbound, Outbound, SDR e AE' },
    ],
  },
  {
    icon: Plug,
    titleKey: 'CRM Integrations',
    titleKeyPt: 'Integrações CRM',
    fullPt: ptIntegrations, fullEn: ptIntegrations,
    thumbPt: ptIntegrationsThumb, thumbEn: ptIntegrationsThumb,
    descEn: 'Connect your CRM in minutes, not weeks. Native integrations with HubSpot and RD Station bring your deals, contacts, companies, and activities into RevOps AI automatically — with custom field support and real-time synchronization. No manual CSV exports, no broken formulas. Your data flows seamlessly and stays in sync every day.',
    descPt: 'Conecte seu CRM em minutos, não semanas. Integrações nativas com HubSpot e RD Station trazem seus deals, contatos, empresas e atividades para o RevOps AI automaticamente — com suporte a campos custom e sincronização em tempo real. Sem exportações manuais de CSV, sem fórmulas quebradas. Seus dados fluem perfeitamente e ficam sincronizados todos os dias.',
    bullets: [
      { en: 'Native integration with RD Station and HubSpot', pt: 'Integração nativa com RD Station e HubSpot' },
      { en: 'Real-time data sync with your CRM', pt: 'Sincronização de dados em tempo real com seu CRM' },
      { en: 'Bi-directional intelligence — reads and writes to your CRM', pt: 'Inteligência bidirecional — lê e escreve no seu CRM' },
      { en: 'CSV import for other CRM systems', pt: 'Importação CSV para outros sistemas de CRM' },
    ],
  },
];

const whyChoose = {
  en: [
    { icon: Target, title: 'Built for RevOps', desc: 'Not a generic BI tool. Every screen answers a specific revenue question. Designed by RevOps professionals with 20+ years of combined experience.' },
    { icon: Zap, title: 'Setup in minutes', desc: 'No consultants, no 6-month implementations. Connect your CRM, map your fields, and get insights on day one.' },
    { icon: Shield, title: 'Data you can trust', desc: 'We use 100% of your CRM data — no sampling, no approximations. Every metric follows a documented, transparent formula.' },
    { icon: Users, title: 'Works for any team size', desc: 'From 3-person sales teams to 100+ rep organizations. Plans designed to grow with you, from Starter to Enterprise.' },
  ],
  pt: [
    { icon: Target, title: 'Feito para RevOps', desc: 'Não é uma ferramenta genérica de BI. Cada tela responde uma pergunta específica de receita. Projetado por profissionais de RevOps com 20+ anos de experiência combinada.' },
    { icon: Zap, title: 'Setup em minutos', desc: 'Sem consultores, sem implementações de 6 meses. Conecte seu CRM, mapeie seus campos e receba insights no primeiro dia.' },
    { icon: Shield, title: 'Dados confiáveis', desc: 'Usamos 100% dos dados do seu CRM — sem amostragem, sem aproximações. Cada métrica segue uma fórmula documentada e transparente.' },
    { icon: Users, title: 'Para qualquer tamanho de time', desc: 'De times de 3 vendedores a organizações de 100+ reps. Planos projetados para crescer com você, do Starter ao Enterprise.' },
  ],
};

export default function FeaturesPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');

  const reasons = isPt ? whyChoose.pt : whyChoose.en;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Funcionalidades — Forecast e Pipeline | RevOps AI" : "Features — Forecast & Pipeline | RevOps AI"}
        description={isPt ? "Dashboards de receita, Sales Analytics, Forecast com IA, Playbooks e integração com RD Station e HubSpot. Tudo para operar receita com previsibilidade." : "Revenue dashboards, Sales Analytics, AI Forecast, Playbooks and integration with RD Station and HubSpot. Everything to operate revenue with predictability."}
        path="/features"
        keywords={isPt ? "funcionalidades RevOps, dashboard receita, forecast IA, sales analytics" : "RevOps features, revenue dashboard, AI forecast, sales analytics"}
      />
      <SchemaMarkup schema={[
        organizationSchema,
        generateSoftwareAppSchema(isPt ? 'pt-BR' : 'en'),
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://revops.digitais.ai/' },
          { name: isPt ? 'Funcionalidades' : 'Features', url: 'https://revops.digitais.ai/features' },
        ]),
      ]} />
      <MarketingNav />
      <main>
        <section className="py-20 md:py-28 bg-[#F4F5FF]">
          <div className="container max-w-3xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Funcionalidades' : 'Features' }]} />
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F3C] mb-6">
                {isPt ? 'Tudo que você precisa para operar receita com previsibilidade' : 'Everything you need to operate revenue with predictability'}
              </h1>
              <p className="text-lg text-[#1A1F3C]/60 mb-8">
                {isPt
                  ? 'Uma plataforma completa de Revenue Intelligence que transforma dados do CRM em diagnósticos, previsões e ações concretas. Projetada por profissionais de RevOps para times que querem crescer com dados, não com intuição.'
                  : 'A complete Revenue Intelligence platform that transforms CRM data into diagnostics, forecasts, and concrete actions. Designed by RevOps professionals for teams that want to grow with data, not gut feel.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                  <Link to="/signup">
                    {isPt ? 'Teste Grátis por 7 Dias' : 'Start 7-Day Free Trial'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/demo">
                    {isPt ? 'Agendar Demo' : 'Book a Demo'}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Sections */}
        {features.map((feature, i) => {
          const Icon = feature.icon;
          const isEven = i % 2 === 0;
          const thumb = isPt ? feature.thumbPt : feature.thumbEn;
          const full = isPt ? feature.fullPt : feature.fullEn;
          return (
            <section key={i} className={`py-16 md:py-20 ${isEven ? 'bg-white' : 'bg-[#F4F5FF]/50'}`}>
              <div className="container max-w-5xl mx-auto">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-[#4F6CFF]/10">
                    <Icon className="h-6 w-6 text-[#4F6CFF]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C]">
                    {isPt ? feature.titleKeyPt : feature.titleKey}
                  </h2>
                </div>
                <p className="text-[#1A1F3C]/70 leading-relaxed mb-8 max-w-3xl">
                  {isPt ? feature.descPt : feature.descEn}
                </p>
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  <div className={isEven ? '' : 'md:order-2'}>
                    <ul className="space-y-4">
                      {feature.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-[#00C896] mt-0.5 flex-shrink-0" />
                          <span className="text-[#1A1F3C]/80 leading-relaxed">{isPt ? b.pt : b.en}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`rounded-xl border border-[#1A1F3C]/10 bg-[#F4F5FF] p-2 shadow-lg ${isEven ? '' : 'md:order-1'}`}>
                    <div className="rounded-lg overflow-hidden bg-[#1A1F3C]">
                      <div className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1F3C] border-b border-white/10">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400/50" />
                      </div>
                      <div className="aspect-[16/10] overflow-hidden">
                        <ImageLightbox
                          src={thumb}
                          fullSrc={full}
                          alt={`${isPt ? feature.titleKeyPt : feature.titleKey} — RevOps AI screenshot`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Why B2B Teams Choose RevOps AI */}
        <section className="py-16 md:py-20 bg-[#F4F5FF]">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C] mb-4 text-center">
              {isPt ? 'Por que times B2B escolhem RevOps AI' : 'Why B2B teams choose RevOps AI'}
            </h2>
            <p className="text-center text-[#1A1F3C]/60 mb-12 max-w-2xl mx-auto">
              {isPt
                ? 'Não somos uma ferramenta genérica de BI nem um dashboard bonito sem profundidade. Somos uma plataforma de Revenue Intelligence construída por quem viveu RevOps na prática.'
                : "We're not a generic BI tool or a pretty dashboard without depth. We're a Revenue Intelligence platform built by people who've lived RevOps in practice."}
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {reasons.map((reason, i) => {
                const Icon = reason.icon;
                return (
                  <div key={i} className="flex gap-4 p-6 rounded-xl bg-white border border-[#1A1F3C]/10">
                    <div className="flex-shrink-0">
                      <div className="p-2 rounded-lg bg-[#4F6CFF]/10">
                        <Icon className="h-5 w-5 text-[#4F6CFF]" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#1A1F3C] mb-1">{reason.title}</h3>
                      <p className="text-sm text-[#1A1F3C]/60 leading-relaxed">{reason.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Platform Summary */}
        <section className="py-16 md:py-20">
          <div className="container max-w-3xl mx-auto prose prose-lg text-[#1A1F3C]/70">
            <h2 className="text-2xl font-bold text-[#1A1F3C]">
              {isPt ? 'Uma plataforma completa para Revenue Operations' : 'A complete platform for Revenue Operations'}
            </h2>
            <p>
              {isPt
                ? 'RevOps AI reúne em um único lugar as seis capacidades essenciais que times de receita precisam para operar com previsibilidade: visibilidade de receita em tempo real, analytics aprofundado de vendas, forecasting matemático, consultoria de IA contextual, playbooks de execução e integração nativa com CRM. Cada funcionalidade foi projetada para responder uma pergunta específica de negócio — eliminando dashboards genéricos e métricas de vaidade. O resultado é uma operação de receita onde cada decisão é baseada em dados, cada risco é identificado antes de virar perda, e cada oportunidade de melhoria vem acompanhada de ação concreta com responsável e prazo definidos.'
                : 'RevOps AI brings together in one place the six essential capabilities that revenue teams need to operate with predictability: real-time revenue visibility, deep sales analytics, mathematical forecasting, contextual AI advisory, execution playbooks, and native CRM integration. Each feature is designed to answer a specific business question — eliminating generic dashboards and vanity metrics. The result is a revenue operation where every decision is data-driven, every risk is identified before it becomes a loss, and every improvement opportunity comes with a concrete action, clear ownership, and defined timeline.'}
            </p>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-[#1A1F3C] text-white text-center">
          <div className="container max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {isPt ? 'Pronto para operar receita com inteligência?' : 'Ready to operate revenue with intelligence?'}
            </h2>
            <p className="text-white/70 mb-8 text-lg">
              {isPt ? 'Comece seu teste gratuito de 7 dias agora.' : 'Start your 7-day free trial now.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                <Link to="/signup">
                  {isPt ? 'Teste Grátis' : 'Start Free Trial'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
                <Link to="/demo">
                  {isPt ? 'Agendar Demo' : 'Book a Demo'}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
